import React, { useState, useEffect, useRef } from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { UcatLayout } from "../../Components/Shared";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { listPracticeExamID } from "../../../../Redux/Slices/PracticeExam/ListPracticeExamIDSlice";
import { addExamAnswerPractice } from "../../../../Redux/Slices/ExamAnswerPractice/AddExamAnswerPracticeSlice";
import { listExamAnswerPractice } from "../../../../Redux/Slices/ExamAnswerPractice/ListExamAnswerPracticeSlice";
import { listExamsubmitPractice } from "../../../../Redux/Slices/ExamAnswerPractice/ListExamsubmitPracticeSlice";
import { addFalg } from "../../../../Redux/Slices/Falg/AddFalgSlice";
import { listFlag } from "../../../../Redux/Slices/Falg/ListFlagSlice";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import {
    SplitPane,
    SinglePageQuestion,
    NavigatorModal,
    ScrollWarningModal,
    ReloadWarningModal,
} from "../../Components/Practice_Test/Exam_component";
import { AnswerType, Question, ExamSection, UserAnswer } from "../../Components/sample data/Type";
import "../../ucat-styles.css";
import { Flag } from "lucide-react";
import { addFalgExam } from "../../../../Redux/Slices/FalgExam/AddFalgExamSlice";
import useNavigationBlocker from "../../../../Hooks/useNavigationBlocker";

// --- Types ---


export const UcatTestScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { state } = location;

    // Persist state to handle reloads
    useEffect(() => {
        if (state && typeof state === 'object') {
            sessionStorage.setItem("ucat_test_state", JSON.stringify(state));
        }
    }, [state]);

    const persistedStateStr = sessionStorage.getItem("ucat_test_state");
    const persistedState = persistedStateStr ? JSON.parse(persistedStateStr) : null;
    const activeState = (state && typeof state === 'object') ? state : persistedState;

    const student_test_id = activeState?.student_test_id;

    // Enable browser navigation blocker
    useNavigationBlocker(true);

    console.warn("student_test_id UTS", student_test_id)
    // --- Redux ---
    const { data: practiceExamData, isLoading: isDataLoading } = useAppSelector(
        (state) => state.ListPracticeExamIDSlice
    );

    const { data: listExamAnswerSlice } = useAppSelector(
        (state) => state.ListExamAnswerPracticeSlice
    );

    const { data: listFlagSlice } = useAppSelector(
        (state) => state.ListFlagSlice
    );

    const { data: addExamAnswerRes } = useAppSelector(
        (state) => state.AddExamAnswerPracticeSlice
    );

    const { data: addFlagRes } = useAppSelector(
        (state) => state.AddFalgSlice
    );

    // --- Local State ---
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(activeState?.questionIndex ?? 0);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [questionId: number]: number }>({});
    const [dragDropAnswers, setDragDropAnswers] = useState<{ [questionId: number]: Record<number, string | null> }>({});
    const [examName, setExamName] = useState("UCAT Practice Test");
    const [isExamLoaded, setIsExamLoaded] = useState(false);
    const [markedQuestions, setMarkedQuestions] = useState<{ [index: number]: boolean }>({});
    const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
    const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
    const [questionStartTime, setQuestionStartTime] = useState<string>(new Date().toISOString());
    const [showScrollWarning, setShowScrollWarning] = useState(false);
    const [lastSeenSubtypeId, setLastSeenSubtypeId] = useState<number | null>(activeState?.subtype_id ?? null);
    const [viewedQuestions, setViewedQuestions] = useState<{ [questionId: number]: boolean }>({});

    useEffect(() => {
        if (questions.length > 0 && questions[currentQuestionIndex]) {
            const qId = questions[currentQuestionIndex].question_id;
            if (!viewedQuestions[qId]) {
                setViewedQuestions(prev => ({ ...prev, [qId]: true }));
            }
        }
    }, [currentQuestionIndex, questions, viewedQuestions]);

    // Navigation blocker state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const blockerInitializedRef = useRef(false);

    useEffect(() => {
        setQuestionStartTime(new Date().toISOString());
    }, [currentQuestionIndex]);

    // --- 1. Fetch Data ---
    useEffect(() => {
        if (student_test_id) {
            const id = Number(student_test_id);
            dispatch(listPracticeExamID({ student_test_id: id }));
            dispatch(listExamAnswerPractice({ student_test_id: id }));
            dispatch(listFlag({ student_test_id: id }));
        }
    }, [dispatch, student_test_id]);

    // Re-fetch when answer or flag is added
    useEffect(() => {
        if (student_test_id) {
            dispatch(listExamAnswerPractice({ student_test_id: Number(student_test_id) }));
        }
    }, [dispatch, student_test_id, addExamAnswerRes]);

    useEffect(() => {
        if (student_test_id) {
            dispatch(listFlag({ student_test_id: Number(student_test_id) }));
        }
    }, [dispatch, student_test_id, addFlagRes]);

    // --- 2. Normalize Data ---
    useEffect(() => {
        const rawData: any = (practiceExamData as any)?.data || practiceExamData;
        const data = Array.isArray(rawData) ? rawData[0] : rawData;

        if (data) {
            setExamName(data.test_name || data.exam_name || "UCAT Practice Test");

            let allQuestions: Question[] = [];
            const sections = data.question_by_type || [];

            if (!isExamLoaded && sections.length > 0) {
                setIsExamLoaded(true);
            }

            sections.forEach((section: any) => {
                if (section.questions) {
                    const mapped = section.questions.map((q: any): Question => ({
                        question_id: q.question_id || q.id,
                        question_type_id: q.question_type_id || section.question_type_id || 0,
                        question_type_name: q.question_type_name || section.question_type_name || "Multiple Choice",
                        question_text: q.question_text || q.question || "",
                        instruction: q.instruction || q.passage || null,
                        prompt: q.prompt || null,
                        prompt_url: q.prompt_url || null,
                        question_url: q.question_url || q.image_url || null,
                        imgWidth: q.imgWidth || 0,
                        prompt_imgWidth: q.prompt_imgWidth || 0,
                        source_type: q.source_type || "practice",
                        subtype_id: q.subtype_id || q.subType || null,
                        subtype_name: q.subtype_name || q.subType_name || null,
                        answer_type: (() => {
                            const type = String(q.answer_type || "").toUpperCase();
                            if (type === "DRAG_DROP" || type === "DRAG-DROP") return AnswerType.DRAG_DROP;
                            if (type === "TRUE_FALSE" || type === "TRUE-FALSE" || type === "YES_NO") return AnswerType.TRUE_FALSE;
                            return AnswerType.MCQ;
                        })(),
                        options: (q.options || []).map((opt: any) => ({
                            option_id: opt.option_id || opt.id,
                            option_text: opt.option_text || opt.text || opt.option || "",
                            option_url: opt.option_url || opt.image_url || null,
                        })),
                        drag_drop_data: q.drag_drop_data || null,
                        drag_drop_pattern: q.drag_drop_pattern || null,
                    }));
                    allQuestions = [...allQuestions, ...mapped];
                }
            });

            setQuestions(allQuestions);
            setIsExamLoaded(true);
        }
    }, [practiceExamData]);

    // --- 3. Sync Answers and Flags ---
    useEffect(() => {
        const answerData = (listExamAnswerSlice?.data as any)?.data || (Array.isArray(listExamAnswerSlice?.data) ? listExamAnswerSlice?.data : []);
        if (answerData && Array.isArray(answerData)) {
            const answers: { [questionId: number]: number } = {};
            const ddAnswers: { [questionId: number]: Record<number, string | null> } = {};
            const viewed: { [questionId: number]: boolean } = {};

            answerData.forEach((ans: any) => {
                const qId = Number(ans.question_id);
                if (ans.option_id) {
                    answers[qId] = Number(ans.option_id);
                }
                // If an answer record exists, the question has been viewed/interacted with
                viewed[qId] = true;

                if (ans.answer_data) {
                    try {
                        ddAnswers[qId] = JSON.parse(ans.answer_data);
                    } catch (e) {
                        console.error("Failed to parse answer_data", ans.answer_data);
                    }
                }
            });

            setSelectedAnswers(answers);
            setDragDropAnswers(ddAnswers);
            setViewedQuestions(prev => ({ ...prev, ...viewed }));
        }
    }, [listExamAnswerSlice]);

    useEffect(() => {
        const responseObj = listFlagSlice?.data as any;
        const flagItems = responseObj?.data;
        const hasFlagsLoaded = Array.isArray(flagItems);

        if (hasFlagsLoaded && questions.length > 0) {
            const marked: { [index: number]: boolean } = {};

            flagItems.forEach((f: any) => {
                if (f.flagged) {
                    const fQId = f.question_id || f.id;
                    const qIdx = questions.findIndex(q => Number(q.question_id) === Number(fQId));
                    if (qIdx !== -1) {
                        marked[qIdx] = true;
                    }
                }
            });
            setMarkedQuestions(marked);
        }
    }, [listFlagSlice, questions]);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation, historyAction }) => {
            // Prevent multiple blocker initializations
            if (!blockerInitializedRef.current) {
                blockerInitializedRef.current = true;
            }

            // 1. ALWAYS block browser back/forward buttons (POP)
            if (historyAction === "POP") {
                console.log("🚫 Blocking browser arrow navigation");
                return true;
            }

            // 2. Allow if we are explicitly submitting/skipping
            if (isSubmittingRef.current) {
                console.log("✅ Allowing navigation (submitting/skipping)");
                return false;
            }

            // 3. Allow if the path isn't changing (e.g. hash changes, which we don't use but good for safety)
            if (currentLocation.pathname === nextLocation.pathname) {
                return false;
            }

            // 4. Specifically check allowed transition paths for programmatic navigation
            const allowedPaths = ["/ucat_exampractice_instruction", "/ucat_examresult_practice", "/ucat_examsummary_practice", "/ucat_test", "/ucat_test_instructions", "/practicetest"];
            const isAllowedTarget = allowedPaths.includes(nextLocation.pathname);

            // Block everything else
            console.warn("BLOCKER", nextLocation.pathname);
            return !isAllowedTarget;
        }
    );

    const [showReloadWarning, setShowReloadWarning] = useState(false);

    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowReloadWarning(true);
        } else {
            setShowReloadWarning(false);
        }
    }, [blocker.state]);

    // Cleanup blocker when component unmounts to prevent multiple blocker conflicts
    useEffect(() => {
        return () => {
            setShowReloadWarning(false);
            if (blocker.state === "blocked") {
                blocker.reset();
            }
        };
    }, [blocker]);

    const handleReloadConfirm = () => {
        // Set submitting flags to prevent further blockers
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        setShowReloadWarning(false);

        // Always reset blocker first to ensure clean navigation
        if (blocker.state === "blocked") {
            blocker.reset();
        }

        // Navigate after a short delay to ensure state updates
        setTimeout(() => {
            navigate('/practicetest');
        }, 100);
    };
    const handleReloadCancel = () => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setShowReloadWarning(false);
    };

    useEffect(() => {
        if (questions.length > 0 && isExamLoaded) {
            const currentQ = questions[currentQuestionIndex];

            // Only trigger if we actually moved to a different question OR it's the first load
            // and the subtype is different from what we last showed instructions for.
            if (currentQ.subtype_id !== lastSeenSubtypeId) {
                // Find instructions for this subtype in practiceExamData
                const currentSection = practiceExamData?.data?.question_by_type?.find(
                    (s: any) => s.question_type_id === currentQ.question_type_id
                );

                const subtype = (currentSection as any)?.subtype_data?.find(
                    (st: any) => (st.subtype_id === currentQ.subtype_id || st.id === currentQ.subtype_id)
                );

                // Calculate the subtype index
                const subtypeIndex = (currentSection as any)?.subtype_data?.findIndex(
                    (st: any) => (st.subtype_id === currentQ.subtype_id || st.id === currentQ.subtype_id)
                ) ?? 0;

                if (subtype && subtype.subtype_instruction && subtype.subtype_instruction.length > 0) {
                    setLastSeenSubtypeId(currentQ.subtype_id);
                    // Navigate to instructions screen instead of setting local state
                    navigate("/ucat_test_instructions", {
                        state: {
                            id: Number(student_test_id),
                            questionIndex: currentQuestionIndex,
                            subtype_id: currentQ.subtype_id,
                            subtype_instructions: subtype.subtype_instruction,
                            subtype_name: subtype.subtype_name,
                            isReturningFromSubtype: true,
                            subtype_flag: true,
                            subtype_index: subtypeIndex
                        }
                    });
                } else {
                    // No instructions, just update the seen ID so we don't check again for this subtype
                    setLastSeenSubtypeId(currentQ.subtype_id);
                }
            }
        }
    }, [currentQuestionIndex, questions, isExamLoaded, practiceExamData, lastSeenSubtypeId]);

    // --- 4. Actions ---
    const handleAnswerSelect = (optionIndex: number) => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        const selectedOption = currentQ.options[optionIndex];
        if (selectedOption) {
            setSelectedAnswers(prev => ({
                ...prev,
                [currentQ.question_id]: selectedOption.option_id
            }));
        }
    };

    const handleDragDropChange = (itemId: number, bucketName: string | null) => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        setDragDropAnswers(prev => ({
            ...prev,
            [currentQ.question_id]: {
                ...(prev[currentQ.question_id] || {}),
                [itemId]: bucketName
            }
        }));
    };

    const saveCurrentAnswer = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        const isDragDrop = currentQ.answer_type === AnswerType.DRAG_DROP;
        const currentSelectedOptionId = selectedAnswers[currentQ.question_id];
        const currentDragDropAnswers = dragDropAnswers[currentQ.question_id];

        // Check if question is answered
        const isAnswered = isDragDrop
            ? currentDragDropAnswers && Object.values(currentDragDropAnswers).some(v => v !== null)
            : currentSelectedOptionId !== undefined;

        const currentTime = new Date().toISOString();

        const payload = {
            student_test_id: Number(student_test_id),
            question_id: currentQ.question_id,
            option_id: isDragDrop ? null : (currentSelectedOptionId || null),
            // remaining_time: formatTime(timeLeft),
            skipped: !isAnswered,
            question_type_id: currentQ.question_type_id,
            question_start_time: questionStartTime,
            question_end_time: currentTime,
            sentence: isDragDrop && isAnswered ? JSON.stringify(currentDragDropAnswers) : null,
            answer_data: isDragDrop && isAnswered ? JSON.stringify(currentDragDropAnswers) : null,
        };
        dispatch(addExamAnswerPractice(payload));
    };

    const handleNext = () => {
        // Check scroll status for content that requires scrolling
        if (!scrollStatus.left || !scrollStatus.right) {
            setShowScrollWarning(true);
            return;
        }

        saveCurrentAnswer();
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((prev: number) => prev + 1);
        } else {
            handleFinish();
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prev: number) => prev - 1);
        }
    };

    const handleFinish = async () => {
        saveCurrentAnswer();

        // Finalize the test
        await dispatch(
            listExamsubmitPractice({
                id: Number(student_test_id),
            })
        );

        // Navigate to Test Result Screen
        navigate("/ucat_examresult_practice", {
            state: {
                studentExamId: student_test_id,
                evalAs: "test",
            }
        });
    };

    const handleToggleFlag = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) return;

        // Optimistic update
        setMarkedQuestions(prev => ({
            ...prev,
            [currentQuestionIndex]: !prev[currentQuestionIndex]
        }));

        // API call
        dispatch(
            addFalg({
                student_test_id: Number(student_test_id),
                question_id: currentQ.question_id,
            })
        );
    };

    // --- Navigator Logic ---
    const getSections = (): ExamSection[] => {
        const sections: ExamSection[] = [];
        let currentSection: ExamSection | null = null;

        questions.forEach((q) => {
            if (!currentSection || currentSection.title !== q.question_type_name) {
                if (currentSection) sections.push(currentSection);
                currentSection = {
                    id: q.question_type_id,
                    title: q.question_type_name,
                    questions: []
                };
            }
            currentSection.questions.push(q);
        });
        if (currentSection) sections.push(currentSection);
        return sections;
    };

    const sections = getSections();

    // Calculate current section and question index within that section
    let currentSectionIndex = 0;
    let questionIndexInSection = 0;
    let count = 0;
    for (let i = 0; i < sections.length; i++) {
        if (currentQuestionIndex < count + sections[i].questions.length) {
            currentSectionIndex = i;
            questionIndexInSection = currentQuestionIndex - count;
            break;
        }
        count += sections[i].questions.length;
    }

    const handleJump = (sectionIdx: number, questionIdx: number) => {
        if (!scrollStatus.left || !scrollStatus.right) {
            setIsNavigatorOpen(false);
            setShowScrollWarning(true);
            return;
        }

        let globalIndex = 0;
        for (let i = 0; i < sectionIdx; i++) {
            globalIndex += sections[i].questions.length;
        }
        globalIndex += questionIdx;
        setCurrentQuestionIndex(globalIndex);
        setIsNavigatorOpen(false);
    };

    const navigatorAnswers: Record<string, UserAnswer> = {};
    questions.forEach((q, idx) => {
        const isDragDrop = q.answer_type === AnswerType.DRAG_DROP;
        const isAnswered = isDragDrop
            ? dragDropAnswers[q.question_id] && Object.values(dragDropAnswers[q.question_id]).some(v => v !== null)
            : selectedAnswers[q.question_id] !== undefined;

        navigatorAnswers[q.question_id] = {
            selectedOptionIndex: selectedAnswers[q.question_id] !== undefined
                ? q.options.findIndex(o => o.option_id === selectedAnswers[q.question_id])
                : null,
            isFlagged: markedQuestions[idx] || false,
            dragDropAnswers: dragDropAnswers[q.question_id] || {},
            isViewed: viewedQuestions[q.question_id] || false
        };
    });

    // --- Keyboard Shortcuts ---
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (showScrollWarning) {
                    setShowScrollWarning(false);
                    e.preventDefault();
                } else if (isNavigatorOpen) {
                    setIsNavigatorOpen(false);
                    e.preventDefault();
                }
            }

            if (e.altKey) {
                switch (e.key.toLowerCase()) {
                    case 'n':
                        e.preventDefault();
                        handleNext();
                        break;
                    case 'p':
                        e.preventDefault();
                        handlePrevious();
                        break;
                    case 'f':
                        e.preventDefault();
                        handleToggleFlag();
                        break;
                    case 'e':
                        e.preventDefault();
                        handleFinish();
                        break;
                    case 'v':
                        e.preventDefault();
                        setIsNavigatorOpen(true);
                        break;
                    case 'o':
                        if (showScrollWarning) {
                            e.preventDefault();
                            setShowScrollWarning(false);
                        }
                        break;
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentQuestionIndex, questions.length, markedQuestions, showScrollWarning, isNavigatorOpen, scrollStatus]);

    // --- Render ---
    if (!isExamLoaded || isDataLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <CircularProgress />
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return <div>No Questions Available</div>;

    const currentSelectedOptionId = selectedAnswers[currentQuestion.question_id];
    const selectedOptionIndex = currentQuestion.options.findIndex(opt => opt.option_id === currentSelectedOptionId);
    const currentDragDropAnswers = dragDropAnswers[currentQuestion.question_id] || {};

    const userAnswer = {
        isFlagged: markedQuestions[currentQuestionIndex] || false,
        selectedOptionIndex: selectedOptionIndex,
        dragDropAnswers: currentDragDropAnswers
    };

    // Decide Layout
    const hasPassage = !!currentQuestion.prompt || !!currentQuestion.prompt_url || !!currentQuestion.instruction;

    // Standard UCAT sections that use split screen
    const isReadingOrVerbal = ["Reading Comprehension", "Verbal Reasoning", "Decision Making"].some(type =>
        currentQuestion.question_type_name.includes(type)
    );

    const shouldUseSplitLayout = (isReadingOrVerbal || hasPassage);

    return (
        <UcatLayout
            headerTitle={examName}
            showToolbar={true}
            headerSubtitle={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
            showCalculatorButton={true}
            showClearFlagButton={true}
            onClearFlagClick={handleToggleFlag}
            footerProps={{
                onNext: handleNext,
                onPrevious: handlePrevious,
                onEndExam: handleFinish,
                onNavigator: () => setIsNavigatorOpen(true),
                showNavigator: true,
                leftButtonText: "",
                rightButtonText: currentQuestionIndex === questions.length - 1 ? "Finish" : "Next",
                previousDisabled: currentQuestionIndex === 0,
                showPrevious: true,
            }}
            toolbarRightActions={

                <button
                    onClick={handleToggleFlag}
                    className="flex items-center gap-1.5 text-white hover:text-[#FFFF00] px-3 h-full transition-all duration-200 group"
                >
                    <Flag
                        size={18}
                        className={
                            userAnswer?.isFlagged
                                ? "fill-[#FFFF00] text-[#FFFF00] drop-shadow-[0_0_8px_rgba(255,255,0,0.5)]"
                                : "text-white/90 group-hover:scale-110 transition-transform"
                        }
                    />
                    <span className="text-[16px] font-medium tracking-tight">
                        <span className="underline decoration-[1.5px] text-[16px] underline-offset-[3px]">F</span>{userAnswer.isFlagged ? "lagged" : "lag"} for Review
                    </span>
                </button>
            }
        >
            <div className="flex-1 overflow-hidden flex flex-col h-full">
                {shouldUseSplitLayout ? (
                    <SplitPane
                        passage={currentQuestion.prompt || currentQuestion.instruction || undefined}
                        promptUrl={currentQuestion.prompt_url || undefined}
                        question={currentQuestion}
                        selectedOptionIndex={selectedOptionIndex}
                        dragDropAnswers={currentDragDropAnswers}
                        onOptionSelect={handleAnswerSelect}
                        onDragDropChange={handleDragDropChange}
                        isReviewMode={false}
                        onScrollStatusChange={setScrollStatus}
                        onLockedClick={() => setShowScrollWarning(true)}
                    />
                ) : (
                    <SinglePageQuestion
                        question={currentQuestion}
                        selectedOptionIndex={selectedOptionIndex}
                        dragDropAnswers={currentDragDropAnswers}
                        onOptionSelect={handleAnswerSelect}
                        onDragDropChange={handleDragDropChange}
                        isReviewMode={false}
                        onScrollStatusChange={setScrollStatus}
                        onLockedClick={() => setShowScrollWarning(true)}
                    />
                )}
            </div>

            <NavigatorModal
                isOpen={isNavigatorOpen}
                onClose={() => setIsNavigatorOpen(false)}
                sections={sections}
                currentSectionIndex={currentSectionIndex}
                currentQuestionIndex={questionIndexInSection}
                answers={navigatorAnswers}
                onJump={handleJump}
            />

            <ScrollWarningModal
                isOpen={showScrollWarning}
                onClose={() => setShowScrollWarning(false)}
            />
            <ReloadWarningModal
                isOpen={showReloadWarning}
                onClose={handleReloadCancel}
                onConfirm={handleReloadConfirm}
            />
        </UcatLayout>
    );
};

export default UcatTestScreen;
